/**
 * Company-Research Review Gate contract — feature E07-F04 (manual verification).
 *
 * The typed wire shapes shared by the api (services + route) and the web (typed
 * api-client + Review screen) for the specialist review surface that sits between
 * the scoring stage and publish:
 *
 *   research_set_status     PATCH — accept / reject / pending (toggle) + grade override
 *   research_add_company    POST  — manual single add (is_manual, needs_human, accepted)
 *   research_bulk_add       POST  — bulk add (one row per line, needs_human, pending)
 *   research_verify_company POST  — enrich + About-Us verify across the 6 criteria
 *   research_submit_review  POST  — close review (job→completed, request→in_review)
 *
 * Parity source (1:1 port): the research prototype
 *   `../EER app/prototype/assets/research.proto.js`
 *   - setReview toggle             L1010-1015
 *   - overrideGrade / editGrade    L1016-1028
 *   - confirmAddCompany            L1030-1045 (manual=true, needsHuman=true,
 *                                   score=null, grade kept, reviewStatus='accepted',
 *                                   breakdown all 'partial')
 *   - verifyCompany                L1049-1070 (each non-pass criterion → pass with a
 *                                   cited /about quote; score 60 if null; grade 'B'
 *                                   if null; employees filled; needsHuman cleared)
 *   - confirmBulkAdd               L1073-1092 (one row per line; a line that is a
 *                                   bare domain `\.[a-z]{2,}` with NO whitespace →
 *                                   raw_domain; reviewStatus='pending')
 *   - filteredCompanies            L857-870 (FULL-list sort by score DESC/ASC, then
 *                                   displayRank=i+1, then grade & status filters)
 * Stage-2 was NEVER deployed to legacy n8n prod, so the prototype + schema.prisma
 * (research_companies / research_jobs / research_requests) are authoritative; there
 * is no verbatim n8n SQL for the review commands. The verify prompt keys come from
 * `ai_prompts` (research_enrich_company / research_verify_about_us).
 *
 * IDOR invariant: owner_id / role / userId are NEVER read from any of these bodies
 * (the etap-1 rule from `DB: Validate Session1`); identity is the session.
 *
 * Feature-owned. The serial Wire pass re-exports this from `@eer/shared`; it MUST
 * NOT edit the shared barrel during parallel work.
 */
import { z } from 'zod';
import { checkStateSchema } from './research-check-state.js';
import { insufficientDataSchema } from './research-insufficient-data.js';
import { priorCollectionSchema } from './research-prior-collection.js';
import { manualCheckMarkSchema } from './research-manual-check.js';
import { fanoutTileSchema } from './research-pipeline.js';
import { prefilterDismissalSchema, prefilterFlagSchema } from './research-prefilter.js';
import { rowLibraryLinkSchema, rowSameCompanySchema, rowTriageSchema } from './research-triage.js';
import { websiteConfirmationSchema } from '../referenceWebsite.js';
/**
 * E07-F02-T19 — a row whose WHOLE-company headcount is outside the request's size
 * window. Discovered hits outside it are cut at search (discovery_excluded); a
 * reference company (the brief's own example) is kept and carries this mark.
 */
export const companySizeFlagSchema = z.object({
    /** The request window as typed ("≤300 globally", "11–500"). */
    window: z.string(),
    /** The whole-company headcount compared with the window. */
    employees: z.number(),
    /** 'group' = the parent group's headcount (the record was a local office). */
    basis: z.enum(['group', 'record']),
    /** The provider name of the group record the number came from (basis 'group'). */
    groupName: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// Review status + grade primitives (mirror schema.prisma research_companies)
// ---------------------------------------------------------------------------
/** research_companies.review_status — pending | accepted | rejected. */
export const REVIEW_STATUSES = ['pending', 'accepted', 'rejected'];
export const reviewStatusSchema = z.enum(REVIEW_STATUSES);
/** Grade chip values — A | B | C (research_companies.grade, char(1)). */
export const REVIEW_GRADES = ['A', 'B', 'C'];
export const reviewGradeSchema = z.enum(REVIEW_GRADES);
/**
 * The 6 breakdown criteria — order matches the prototype mini-chips and the
 * scoring contract (services first in the weight order; here we keep the
 * prototype display order industry → subIndustry → geo → size → founded →
 * services used by the breakdown legend, T05).
 */
export const REVIEW_CRITERIA_KEYS = [
    'industry',
    'subIndustry',
    'geo',
    'size',
    'founded',
    'services',
];
/** Legend rows for the breakdown-legend strip (6 squares, T05 acceptance). */
export const REVIEW_CRITERIA_LEGEND = [
    { key: 'industry', short: 'Ind', label: 'Industry' },
    { key: 'subIndustry', short: 'Sub', label: 'Sub-industry' },
    { key: 'geo', short: 'Geo', label: 'Geo (radius)' },
    { key: 'size', short: 'Size', label: 'Company size' },
    { key: 'founded', short: 'Fnd', label: 'Founded window' },
    { key: 'services', short: 'Svc', label: 'Services match (About-Us)' },
];
/**
 * A breakdown verdict from a MANUAL upgrade (the review VerifyAdapter INPUT type).
 * Deliberately narrow — pass|partial|fail — because a manual/citation-or-reject
 * upgrade may never produce 'unclear' (see BUG-1 ADR 2026-07-16). Do NOT widen
 * this: it guards the manual-action paths (VerifyAdapter.upgraded[].verdict).
 */
export const REVIEW_VERDICTS = ['pass', 'partial', 'fail'];
export const reviewVerdictSchema = z.enum(REVIEW_VERDICTS);
/**
 * The STORED / WIRE verdict union — pass|partial|fail PLUS 'unclear'. The real
 * anti-hallucination verify-runner (`research_verify_about_us`) legitimately
 * emits 'unclear' when it can find no citable evidence (~30% of criteria on live
 * runs), and the backend's `deriveReviewNeedsHuman` already reads it as a normal
 * value. This is the shape READ from the DB and serialised to the client, so it
 * MUST accept 'unclear' — otherwise a single 'unclear' row 400s the whole
 * `GET /research/:id/review` list (BUG-1). Kept separate from `reviewVerdictSchema`
 * so the manual-upgrade input contract stays narrow.
 */
export const REVIEW_STORED_VERDICTS = ['pass', 'partial', 'fail', 'unclear'];
export const reviewStoredVerdictSchema = z.enum(REVIEW_STORED_VERDICTS);
/** breakdown JSON: criterion → stored verdict (may be 'unclear'). */
export const reviewBreakdownSchema = z.record(z.enum(REVIEW_CRITERIA_KEYS), reviewStoredVerdictSchema);
/** A single evidence entry per criterion (stored verdict + verbatim quote + source URL). */
export const reviewEvidenceEntrySchema = z.object({
    verdict: reviewStoredVerdictSchema,
    quote: z.string().nullable().default(null),
    url: z.string().nullable().default(null),
});
/** The full evidence map (subset of criteria; not every key is always present). */
export const reviewEvidenceSchema = z.record(z.string(), reviewEvidenceEntrySchema);
// ---------------------------------------------------------------------------
// The Review-table row DTO (what the SPA renders)
// ---------------------------------------------------------------------------
/** A long-list row as the Review screen consumes it. */
/** E06-F03-T13-S03 — does the row have a website (see `reviewRowSchema.websiteStatus`). */
export const websiteStatusSchema = z.enum(['known', 'not_found']);
/** E06-F03-T13-S03 — provenance of a known website (see `reviewRowSchema.websiteSource`). */
export const websiteSourceSchema = z.enum(['lookup', 'manual']);
/**
 * E07-F04-T13 — where the company is, as the free company-data search (ZoomInfo)
 * reported it at discovery time: country, city and the HQ country. Every field is
 * nullable — a provider that did not return it leaves it null (the UI shows a dash,
 * never a guess). Stored in the row's private discovery provenance, no DB column.
 */
export const companyLocationSchema = z.object({
    country: z.string().nullable().default(null),
    city: z.string().nullable().default(null),
    hqCountry: z.string().nullable().default(null),
    /**
     * E12-F17-T02 — state / province of the address (ZoomInfo `state` → `CompanyHit.region`).
     * Optional so rows stored before the field existed stay valid (read back as null).
     */
    state: z.string().nullable().optional(),
});
/**
 * E12-F17-T02 — the "State/City" text of a location: `"<city>, <state>"` when both are
 * known, the one that is known otherwise, null when neither (never "null"/"undefined").
 * The ONE format shared by the long-list export and the Review "City" cell. Pure.
 */
export function formatCityState(loc) {
    const clean = (v) => typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
    const parts = [clean(loc?.city), clean(loc?.state)].filter((v) => v !== null);
    return parts.length > 0 ? parts.join(', ') : null;
}
export const reviewRowSchema = z.object({
    id: z.number().int(),
    /**
     * The server-resolved display name (BUG-5): company.canonicalName → rawName →
     * rawDomain → 'Unnamed company'. Resolved on the backend (single source of
     * truth, parity with the Evidence-drawer) so the table never has to reconstruct
     * it from the raw fields (which go null after the row resolves to a Company).
     */
    name: z.string(),
    /**
     * E07-F04-T09 — the server-resolved website: `companies.domain` → `rawDomain` →
     * null. Same reason as `name`: `raw_domain` goes NULL once the row resolves to a
     * Company, so the table printed "no website" for companies whose domain we know.
     * The UI renders THIS field; `rawDomain` stays for provenance/debug only.
     */
    domain: z.string().nullable().default(null),
    /**
     * E06-F03-T13-S03 — `known` when `domain` is set, `not_found` when it is null. The
     * UI label for `not_found` is WEBSITE_NOT_FOUND_LABEL ("Website not found" /
     * «сайт не найден») — NEVER "no website": we did not find it, the company has one.
     * Optional so older payloads/fixtures stay valid; the server always sends it.
     */
    websiteStatus: websiteStatusSchema.optional(),
    /**
     * E06-F03-T13-S03 — where a known website came from: `lookup` = the automatic free
     * name search, `manual` = typed in Review (PATCH …/website); null = discovery,
     * manual add, or unknown. The server always sends it.
     */
    websiteSource: websiteSourceSchema.nullable().optional(),
    /**
     * E06-F03-T14 — a reference company's website: `unconfirmed` while it is the one
     * code found and nobody checked it, `confirmed` once a person confirmed or typed
     * it; null for a non-reference row or a row without a website (referenceWebsite.ts).
     */
    websiteConfirmation: websiteConfirmationSchema.nullable().optional(),
    rawName: z.string().nullable().default(null),
    rawDomain: z.string().nullable().default(null),
    score: z.number().nullable().default(null), // NUMERIC(5,2) — null for unscored/manual
    grade: reviewGradeSchema.nullable().default(null),
    breakdown: reviewBreakdownSchema.default({}),
    evidence: reviewEvidenceSchema.default({}),
    reviewStatus: reviewStatusSchema.default('pending'),
    needsHuman: z.boolean().default(false),
    /**
     * E07-F04-T11 — derived check state (research-check-state.ts): a row no AI check /
     * Verify has touched is `not_checked`, distinct from `verified` and `needs_human`.
     * Default 'verified' only keeps an older API payload (no field) parseable.
     */
    checkState: checkStateSchema.default('verified'),
    /**
     * E07-F01-T11 — set when a check ran but found nothing to judge by (site did not
     * open / no readable text / model returned no evidence): the row is NOT a 0% "does
     * not fit" — `score` is null and no automatic grade is given. null otherwise.
     * Optional so older payloads/fixtures parse (research-insufficient-data.ts).
     */
    insufficientData: insufficientDataSchema.nullable().optional(),
    isManual: z.boolean().default(false),
    /**
     * E06-F03-T13-S02 — a reference company from the intake (criteria.competitors,
     * seeded by discovery). Kept on the ONE merged row when discovery also found it.
     * The server always sends it; optional (absent = not a reference) so older
     * clients/fixtures built before the field stay valid.
     */
    isReference: z.boolean().optional(),
    /**
     * E07-F04-T12 — the company was already checked in an earlier request: when, where
     * (a link target) and whether that verdict was reused here. null otherwise.
     * Optional so older payloads/fixtures parse (research-prior-collection.ts).
     */
    priorCollection: priorCollectionSchema.nullable().optional(),
    /**
     * E07-F04-T13 — the company location from discovery (see companyLocationSchema);
     * null when the provider returned none. Optional so older payloads/fixtures parse.
     */
    location: companyLocationSchema.nullable().optional(),
    /**
     * E07-F04-T14 — the discovery tile (sub-industry × geo × size) whose search found
     * this company; null for a reference seed, a manual/bulk add, or an older row.
     * Read from the private discovery provenance. Optional so older payloads parse.
     */
    tile: fanoutTileSchema.nullable().optional(),
    /**
     * E07-F04-T14 — the company's industry as the company-data search reported it
     * (ZoomInfo primary industry); null when not reported. Optional (older payloads).
     */
    industry: z.string().nullable().optional(),
    /**
     * E07-F02-T16 — the row was found only by the relaxed-key search (the tile's strict
     * search ran out below its ceiling and was repeated with a looser industry filter):
     * the keyword that search used. Null for a strict-search row. Optional (older payloads).
     */
    relaxedKey: z.string().nullable().optional(),
    /**
     * E07-F02-T19 — the company's whole headcount is outside the size window (a kept
     * reference company). Null = inside / unknown / no window. Optional (older payloads).
     */
    sizeOutside: companySizeFlagSchema.nullable().optional(),
    /**
     * E06-F05-T16 — the check data were entered by hand (site blocked the AI check):
     * who, when, which page. null/absent otherwise. Optional (older payloads).
     */
    manualCheck: manualCheckMarkSchema.nullable().optional(),
    /**
     * E06-F07-T01 — stage-0 prefilter flags (rule + reason; the row is kept). Empty /
     * absent = not flagged. Optional so older payloads/fixtures parse.
     */
    prefilterFlags: z.array(prefilterFlagSchema).optional(),
    /**
     * E06-F07-T02 — flags a person returned ("Return") for this request; a re-run of
     * the prefilters never puts them back. Optional (older payloads).
     */
    prefilterDismissed: z.array(prefilterDismissalSchema).optional(),
    /**
     * E06-F08-T02 — the Jev triage of the row (bucket + reason + probabilities, or the
     * queued / running status while the job works). null/absent = not triaged.
     */
    triage: rowTriageSchema.nullable().optional(),
    /**
     * E06-F10-T01 — Jev hints for the row's ambiguous pairs with the Library («возможно, та же
     * компания, что <X> (0,93)»). A hint only; null/absent = not asked.
     */
    sameCompany: rowSameCompanySchema.nullable().optional(),
    /** E06-F10-T01 — the last human «связать» (row → Library company). null/absent = never. */
    libraryLink: rowLibraryLinkSchema.nullable().optional(),
    rationale: z.string().nullable().default(null),
    /** Assigned client-side after the full-list sort (1..N, score-monotonic). */
    displayRank: z.number().int().nullable().default(null),
});
// ---------------------------------------------------------------------------
// research_set_status (T01) — PATCH accept/reject toggle + grade override
// ---------------------------------------------------------------------------
/**
 * One review action. `kind:'status'` toggles review_status (the prototype rule:
 * `co.reviewStatus===status ? 'pending' : status`); `kind:'grade'` overrides the
 * grade (A|B|C) and is logged as a human override (audit, T06). NB: owner_id /
 * role are intentionally absent — identity is the session (IDOR).
 */
export const setStatusBodySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('status'),
        status: reviewStatusSchema,
        /**
         * E07-F10-T01 — the person decided on THIS row (default true): the decision becomes a Jev
         * label. A client sends `false` for a decision made without looking at the row (spec §5.6:
         * a batch approval by grade is not a label, else Jev learns from itself).
         */
        opened: z.boolean().optional(),
        /** E07-F10-T04 — optional short reason («дистрибьютор»), shown to Jev as an example. */
        reason: z.string().trim().max(500).optional(),
    }),
    z.object({
        kind: z.literal('grade'),
        grade: reviewGradeSchema,
        reason: z.string().trim().max(500).optional(),
    }),
]);
/**
 * E07-F10-T01 — POST /research/:requestId/review/accept-by-grade: accept every PENDING row of a
 * grade in one click. A batch decision, so it is never a Jev label (spec §5.6).
 */
export const acceptByGradeBodySchema = z.object({ grade: reviewGradeSchema }).strict();
export const acceptByGradeResponseSchema = z.object({
    success: z.literal(true).optional(),
    grade: reviewGradeSchema,
    accepted: z.number().int().nonnegative(),
});
export const setStatusResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    row: reviewRowSchema,
    /** True when this set toggled the status back to pending (re-click). */
    toggledToPending: z.boolean().default(false),
    /** True when a grade override was applied + audited. */
    gradeOverridden: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// research_add_company (T02) — manual single add
// ---------------------------------------------------------------------------
export const addCompanyBodySchema = z.object({
    name: z.string().trim().min(1).max(500),
    site: z.string().trim().max(255).optional().default(''),
    grade: reviewGradeSchema.nullable().optional().default(null),
});
export const addCompanyResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    row: reviewRowSchema,
});
// ---------------------------------------------------------------------------
// research_bulk_add (T02) — bulk add (one row per line)
// ---------------------------------------------------------------------------
export const bulkAddBodySchema = z.object({
    /** The raw multiline paste; the service splits/trims/dedups empty lines. */
    text: z.string().min(1),
});
export const bulkAddResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    rows: z.array(reviewRowSchema),
    added: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// E06-F03-T13-S03 — enter a website the automatic lookup did not find
// ---------------------------------------------------------------------------
/** PATCH /research/:requestId/review/:rowId/website — `acme.com` or a full URL. */
export const setWebsiteBodySchema = z.object({
    website: z.string().trim().min(1).max(255),
});
export const setWebsiteResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    row: reviewRowSchema,
});
/**
 * E06-F03-T14 — POST /research/:requestId/review/:rowId/website/confirm (no body):
 * a person confirms the code-found website of a reference company. Same response
 * shape as the website PATCH (`{ success, row }`).
 */
export const confirmWebsiteResponseSchema = setWebsiteResponseSchema;
/**
 * E06-F10-T01 — POST /research/:requestId/review/:rowId/link-company (body:
 * linkRowCompanyBodySchema): the row re-pointed at an existing Library company by a person.
 * `changed` = false when the row was already linked to it.
 */
export const linkRowCompanyResponseSchema = setWebsiteResponseSchema.extend({
    changed: z.boolean(),
});
/**
 * E06-F07-T02 — POST /research/:requestId/review/:rowId/prefilter/dismiss|restore
 * (body: prefilterFlagActionBodySchema): the updated row, same shape as above.
 */
export const prefilterFlagResponseSchema = setWebsiteResponseSchema;
/**
 * E07-F10-T01 — POST /research/:requestId/review/:rowId/triage/restore: a person returns a row
 * from «вероятный мусор» (bucket → check). The updated row, same shape as above.
 */
export const triageRestoreResponseSchema = setWebsiteResponseSchema;
// ---------------------------------------------------------------------------
// research_verify_company (T03) — enrich + About-Us verify
// ---------------------------------------------------------------------------
export const verifyCompanyResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    row: reviewRowSchema,
});
// ---------------------------------------------------------------------------
// research_submit_review (T04) — close the gate
// ---------------------------------------------------------------------------
export const submitReviewResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    jobId: z.number().int(),
    jobStatus: z.literal('completed'),
    requestId: z.number().int(),
    // E07-F04-T17 — a re-submit (review already closed) of a request that was
    // already published keeps 'published'; every other submit lands on 'in_review'.
    requestStatus: z.enum(['in_review', 'published']),
});
// ---------------------------------------------------------------------------
// research review list (T05) — the table payload
// ---------------------------------------------------------------------------
export const reviewListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    rows: z.array(reviewRowSchema),
    /** Count-badge summary for the summary strip. */
    summary: z.object({
        total: z.number().int().nonnegative(), // rows on the long-list
        accepted: z.number().int().nonnegative(),
        needsHuman: z.number().int().nonnegative(),
        // E07-F04-T11: rows no AI check / Verify has touched (checkState='not_checked').
        notChecked: z.number().int().nonnegative().default(0),
        // E07-F04-T10: the LIVE long-list size (== rows.length), NOT the stored
        // research_jobs.total_companies snapshot, which lags every manual/bulk add.
        discovered: z.number().int().nonnegative(),
    }),
    /** Review mode of the parent request (gate | auto) — drives the Submit button. */
    reviewMode: z.enum(['gate', 'auto']),
});
// ---------------------------------------------------------------------------
// Filter primitives (T05) — used by both the service & the web model
// ---------------------------------------------------------------------------
// E07-F04-T18: 'scope' = the grades inside the request's Grade scope (A | A+B |
// A+B+C) — the Review default; 'ungraded' = rows with no grade yet (not AI-checked,
// insufficient data, or below the scope cut).
export const reviewGradeFilterSchema = z.enum(['all', 'scope', 'A', 'B', 'C', 'ungraded']);
export const reviewStatusFilterSchema = z.enum(['all', 'accepted', 'rejected', 'pending']);
export const reviewSortDirSchema = z.enum(['desc', 'asc']);
/** A bare-domain line (`\.[a-z]{2,}` and NO whitespace) → treat as a site/domain. */
export const BULK_DOMAIN_REGEX = /\.[a-z]{2,}/i;
//# sourceMappingURL=research-review.js.map